/**
 * placement_rules — the SWG placement map's judgement, PURE (2026-09-23).
 *
 * "Green is where you can place it and red is not; you cannot place it in water nor
 * colliding with other structures, and the entrance has to be accessible." The rules
 * MIRROR the server's `placementProblem` (server/src/lib/structures.ts) rule for rule,
 * in the same order, over the same footprint table (@shared/types/structures.ts) — so
 * the colour under the cursor is the verdict the reducer will give — plus what only the
 * client can know (lakes and rivers are client-side bakes; settlement buildings live in
 * the footprint bakes, not the structure table). SWG's three colours:
 *   green  — it may stand here;
 *   red    — a TERRAIN reason (slope, water, a cliff at the door, a structure in the way);
 *   yellow — a NON-terrain restriction (a civic piece outside its city, a City Hall too
 *            close to another city, a rank gate). Only green places.
 * Everything here takes plain numbers and callbacks; the module owns the scene, the
 * harness (verify:placement-rules) drives it with fakes.
 */
import { footprintOf, doorApproach, CIVIC_MIN_RANK, FOOTPRINT_MAX_HALF_DIAG } from '@shared/types/structures.js';
import { obbsMeet, type Obb } from './settlement_arrange.js';
import { obbTouchesDisc, obbInsideDisc, discsMeet, playerGuildRadius, isGuildStructureType, GUILD_STRUCTURE_MIN_TIER } from './guild_rules.js';

// ─── Server mirrors (server/src/lib/constants.ts + terrain.ts) ───────────────────────────────
/** The AOI cell (no-build zones are keyed by it). ONE STRUCTURE PER CELL IS NO LONGER A RULE (2026-09-23): a
 *  64 m cell could hold one cottage; SWG cities pack buildings a few metres apart, and the footprint overlap
 *  test is what keeps them from touching. */
export const CELL_M = 64;
/** `regionNoBuild` keys terrain_region rows by REGION_SIZE_M cells. */
export const REGION_SIZE_M = 256;
export const HOUSE_MAX_SLOPE = 0.35;
export const INDUSTRIAL_MAX_SLOPE = 0.28;
/** `terrainSlope`: the height RANGE over a ±6 m 3×3 window ÷ 12. */
export const SLOPE_WINDOW_M = 6;
/** A City Hall must stand this far from every other city's centre (CITY_HALL_MIN_SPACING_M). */
export const CITY_HALL_MIN_SPACING_M = 1000;
/** Water: ground at or under the sea level + this margin is "in the water" (the server's test). */
export const WATER_MARGIN_M = 0.5;
/** Two footprints must keep this gap (the server's overlap gap); the door step tests with none. */
export const FOOTPRINT_GAP_M = 1;
/** The door probe's own half-size (a person standing at the approach point). */
export const DOOR_STEP_HALF_M = 0.6;
/** The industrial placement classes (server STRUCTURE_CATALOG placementClass: 'industrial'). */
export const INDUSTRIAL_TYPES: ReadonlySet<string> = new Set([
  'factory', 'harvester', 'extractor_small', 'extractor_medium', 'extractor_large', 'shuttleport', 'bunker', 'turret',
  'city_wall', 'spaceport', 'mining_facility',
]);

/** The standing water the server names in its refusal (shared/water_rules.ts StandingKind). */
export type StandingWaterKind = 'river' | 'lake' | 'pond';
/** The server's words (server/src/lib/structures.ts IN_STANDING_WATER / DOOR_ON_STANDING_WATER — pinned by
 *  verify:placement-water), so the reason on the map is the reason the reducer gives. */
export const IN_STANDING_WATER: Record<StandingWaterKind, string> = { river: 'in the river', lake: 'in the lake', pond: 'in a pond' };
export const DOOR_ON_STANDING_WATER: Record<StandingWaterKind, string> = { river: 'the door opens onto the river', lake: 'the door opens onto the lake', pond: 'the door opens onto a pond' };

export type PlacementColor = 'green' | 'red' | 'yellow';
export const PLACEMENT_HEX: Record<PlacementColor, number> = { green: 0x3fb950, red: 0xf85149, yellow: 0xe3b341 };

export interface PlacedObb extends Obb { type: string }

/** What the judge asks the world (absolute coordinates everywhere). */
export interface PlacementWorld {
  /** The ground height the SERVER sees (the analytic terrain on the Commons). */
  groundY(x: number, z: number): number;
  /** The sea level (null when the world has no sea — a personal world). */
  seaLevel: number | null;
  /** Still water over (x, z) — lakes and rivers included (client-only knowledge); null when dry. */
  waterAt(x: number, z: number): number | null;
  /** STANDING WATER AS THE SERVER JUDGES IT (placement_water_v2, 2026-10-01): the river / baked lake / pond the
   *  server's `placementProblem` would refuse at (x, z) — lib/placement_water.ts serverStandingWater, the same rule
   *  on the same inputs. Absent = no such rule (not the Commons, or the flag is off): the judge is the old judge. */
  standingWaterAt?(x: number, z: number): StandingWaterKind | null;
  /** Every deployed structure's footprint near the spot (the structure table), plus what only the client knows
   *  (settlement buildings, dressing solids) — anything a footprint may not overlap. */
  obstaclesNear(x: number, z: number, reach: number): PlacedObb[];
  /** A no-build zone (the cell table) covers the spot (a world without `protectedAt` may fold its regions in here). */
  noBuildAt(x: number, z: number, cx: number, cz: number): boolean;
  /** A protected terrain region (terrain_region.noBuild) covers the spot — the server's `regionNoBuild`, judged BEFORE
   *  the zone cell and named "a protected region" (2026-09-25: the map called it "a no-build zone"). */
  protectedAt?(x: number, z: number): boolean;
  /** Player cities, for the yellow rules (`status` 'invalid' = a city below five residents, awaiting its pulse). */
  cities: Array<{ cityId: bigint; centerX: number; centerZ: number; radiusM: number; cityRank: number; name?: string; status?: string }>;
  /** GUILD LAND (2026-09-24): every guild's claim, with whether the builder is a member (the server's build rights). */
  claims?: GuildClaim[];
  /** Settlement plateaus (radius + half the blend ring) — a guild's claim stays off them. */
  plateaus?: Array<{ x: number; z: number; r: number }>;
  /** How many structures of OTHER owners (the builder's guild's own property excepted) stand on a disc. */
  strangersOn?(x: number, z: number, r: number): number;
}

/** A guild's claim as the judge sees it: `name` is the server's wording ("the Iron Writ guild", "the Tillers of Veldea"). */
export interface GuildClaim { key: string; kind: 'player' | 'versona'; guildId: bigint; name: string; x: number; z: number; r: number; member: boolean }

export interface PlacementRequest {
  type: string;
  x: number; z: number;
  /** three's rotation about Y, radians. */
  yaw: number;
  /** A civic / decor piece placed through a city's console (the mayor's path): its city. */
  civicCityId?: bigint | null;
  /** GUILD PROPERTY: raised on this guild's claim by its court (placeGuildStructure), and the claim's tier. */
  guildId?: bigint | null;
  guildTier?: number;
}

export interface PlacementVerdict { color: PlacementColor; why: string }

/** The footprint's oriented box at a pose (the structure frame; the model yaw never enters). */
export function footprintObb(type: string, x: number, z: number, yaw: number): Obb {
  const fp = footprintOf(type);
  return { x, z, hx: fp.hx, hz: fp.hz, yaw };
}

/** The four corners + the centre (the server's water sample set). */
export function footprintSamples(o: Obb): Array<[number, number]> {
  const c = Math.cos(o.yaw), s = Math.sin(o.yaw);
  const out: Array<[number, number]> = [[o.x, o.z]];
  for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as Array<[number, number]>) {
    const lx = sx * o.hx, lz = sz * o.hz;
    out.push([o.x + c * lx + s * lz, o.z - s * lx + c * lz]);
  }
  return out;
}

/** The server's `terrainSlope`: the height range over a 3×3 window ±SLOPE_WINDOW_M, over its span. */
export function slopeAt(groundY: (x: number, z: number) => number, x: number, z: number): number {
  let lo = Infinity, hi = -Infinity;
  for (let ix = -1; ix <= 1; ix++) for (let iz = -1; iz <= 1; iz++) {
    const h = groundY(x + ix * SLOPE_WINDOW_M, z + iz * SLOPE_WINDOW_M);
    if (h < lo) lo = h;
    if (h > hi) hi = h;
  }
  return (hi - lo) / (2 * SLOPE_WINDOW_M);
}

export function worldToCell(v: number): number { return Math.floor(v / CELL_M); }

/** Is this type placed through a city (SWG's civic set) rather than as a free deed? */
export function isCivicType(type: string): boolean { return type in CIVIC_MIN_RANK && type !== 'city_hall'; }

/** THE JUDGE. Red rules in the server's order, then the client-only red, then yellow. */
export function judgePlacement(w: PlacementWorld, r: PlacementRequest): PlacementVerdict {
  const fp = footprintOf(r.type);
  const mine = footprintObb(r.type, r.x, r.z, r.yaw);
  const maxSlope = INDUSTRIAL_TYPES.has(r.type) ? INDUSTRIAL_MAX_SLOPE : HOUSE_MAX_SLOPE;
  if (slopeAt(w.groundY, r.x, r.z) > maxSlope) return { color: 'red', why: 'too steep' };
  const cx = worldToCell(r.x), cz = worldToCell(r.z);
  // THE SERVER'S WORDS AND ORDER (2026-09-25): `regionNoBuild` ('a protected region') before the zone cell.
  if (w.protectedAt?.(r.x, r.z)) return { color: 'red', why: 'a protected region' };
  if (w.noBuildAt(r.x, r.z, cx, cz)) return { color: 'red', why: 'a no-build zone' };
  // Water — the server's sea test on the centre and corners, then the client's lakes and rivers.
  const samples = footprintSamples(mine);
  if (w.seaLevel !== null) {
    for (const [px, pz] of samples) if (w.groundY(px, pz) <= w.seaLevel + WATER_MARGIN_M) return { color: 'red', why: 'in the water' };
  }
  // STANDING WATER, AS THE SERVER JUDGES IT (2026-10-01): the server now refuses a river, a baked lake and a pond at
  // these same points — asked here on the f32 pose the reducer receives (posX / posZ / yaw are f32 on the wire: at
  // 480 km a metre has 32 steps), with the server's words. BEFORE the rendered-water test below, which stays: it also
  // refuses rendered water the server's flood does not hold (stricter, never greener).
  const fx = Math.fround(r.x), fz = Math.fround(r.z), fyaw = Math.fround(r.yaw);
  if (w.standingWaterAt) {
    for (const [px, pz] of footprintSamples({ x: fx, z: fz, hx: fp.hx, hz: fp.hz, yaw: fyaw })) {
      const k = w.standingWaterAt(px, pz);
      if (k) return { color: 'red', why: IN_STANDING_WATER[k] };
    }
  }
  for (const [px, pz] of samples) if (w.waterAt(px, pz) !== null) return { color: 'red', why: 'in the water' };
  // Overlap — every footprint within reach, deployed or settlement.
  // The largest neighbour's half-diagonal past our own (the server's reach — a guild hall reaches 64 m from its centre).
  const reach = Math.hypot(fp.hx, fp.hz) + FOOTPRINT_MAX_HALF_DIAG + 2;
  const near = w.obstaclesNear(r.x, r.z, reach);
  for (const o of near) if (obbsMeet(mine, o, FOOTPRINT_GAP_M)) return { color: 'red', why: 'overlapping another structure' };
  // The door — dry, walkable, unblocked.
  const door = doorApproach(r.x, r.z, r.yaw, fp.hz);
  if (w.seaLevel !== null && w.groundY(door.x, door.z) <= w.seaLevel + WATER_MARGIN_M) return { color: 'red', why: 'the door opens onto water' };
  if (w.standingWaterAt) {
    const fd = doorApproach(fx, fz, fyaw, fp.hz);
    const k = w.standingWaterAt(fd.x, fd.z);
    if (k) return { color: 'red', why: DOOR_ON_STANDING_WATER[k] };
  }
  if (w.waterAt(door.x, door.z) !== null) return { color: 'red', why: 'the door opens onto water' };
  if (slopeAt(w.groundY, door.x, door.z) > maxSlope) return { color: 'red', why: 'the door opens onto a cliff' };
  const step: Obb = { x: door.x, z: door.z, hx: DOOR_STEP_HALF_M, hz: DOOR_STEP_HALF_M, yaw: 0 };
  for (const o of near) if (obbsMeet(step, o, 0)) return { color: 'red', why: 'another structure blocks the door' };
  // THE NEIGHBOUR'S DOOR (2026-09-25, mirrors the server's placementProblem): the new footprint may not stand on an
  // existing STRUCTURE's approach point (a 0.6 m step 2.5 m beyond its −Z face, no gap) — only the new building's own
  // door was protected, so a store could wall a house's owner in. Structure rows within the overlap reach only (the
  // server's `near` list); a settlement solid's −Z face is no door.
  for (const o of near) {
    if (o.type === 'solid' || Math.abs(o.x - r.x) > reach || Math.abs(o.z - r.z) > reach) continue;
    const d = doorApproach(o.x, o.z, o.yaw, o.hz);
    if (obbsMeet(mine, { x: d.x, z: d.z, hx: DOOR_STEP_HALF_M, hz: DOOR_STEP_HALF_M, yaw: 0 }, 0)) return { color: 'red', why: "blocking a neighbour's door" };
  }
  // Yellow — the non-terrain restrictions.
  const claims = w.claims ?? [];
  if (r.guildId != null) {
    // GUILD PROPERTY: the type, the claim's tier, the whole footprint on the claim (placeGuildStructure's order).
    if (!isGuildStructureType(r.type)) return { color: 'yellow', why: 'a guild does not raise this on its land' };
    const own = claims.find((c) => c.kind === 'player' && c.guildId === r.guildId);
    if (!own) return { color: 'yellow', why: 'the guild holds no claim' };
    const need = GUILD_STRUCTURE_MIN_TIER[r.type] ?? 1;
    if ((r.guildTier ?? 1) < need) return { color: 'yellow', why: `needs a tier-${need} claim` };
    if (!obbInsideDisc(mine, own)) return { color: 'yellow', why: `the whole building must stand inside the claim (${own.r} m)` };
    return { color: 'green', why: '' };
  }
  for (const c of claims) {
    if (!obbTouchesDisc(mine, c)) continue;
    if (c.kind === 'player' && c.member) continue;
    return { color: 'yellow', why: c.kind === 'player' ? `inside ${c.name}'s territory — members only` : `on ${c.name}'s land` };
  }
  if (r.type === 'guild_hall') {
    const r1 = playerGuildRadius(1);
    const me = { x: r.x, z: r.z, r: r1 };
    const pre = `a guild hall claims ${r1} m around it — `;
    for (const c of claims) if (discsMeet(me, c)) return { color: 'yellow', why: `${pre}too near ${c.name}'s claim` };
    for (const p of w.plateaus ?? []) if (discsMeet(me, p)) return { color: 'yellow', why: `${pre}on a settlement's lands — a guild claims open country` };
    for (const c of w.cities) {
      if (c.status === 'invalid') continue; // the server's claimProblem skips a city out of standing (2026-09-25: the map still refused its border)
      if (discsMeet(me, { x: c.centerX, z: c.centerZ, r: c.radiusM })) return { color: 'yellow', why: `${pre}inside ${c.name || 'a city'}'s city border` };
    }
    const n = w.strangersOn ? w.strangersOn(r.x, r.z, r1) : 0;
    if (n > 0) return { color: 'yellow', why: `${pre}${n} building${n === 1 ? '' : 's'} of others stand there` };
  }
  if (r.type === 'city_hall') {
    for (const c of w.cities) {
      if (Math.hypot(c.centerX - r.x, c.centerZ - r.z) < CITY_HALL_MIN_SPACING_M) return { color: 'yellow', why: `too close to another city (${CITY_HALL_MIN_SPACING_M} m)` };
    }
  } else if (isCivicType(r.type)) {
    const c = r.civicCityId != null ? w.cities.find((k) => k.cityId === r.civicCityId) : undefined;
    if (!c) return { color: 'yellow', why: 'a civic piece is placed through its city console' };
    if (Math.hypot(c.centerX - r.x, c.centerZ - r.z) > c.radiusM) return { color: 'yellow', why: `outside the city border (${c.radiusM} m)` };
    const minRank = CIVIC_MIN_RANK[r.type] ?? 1;
    if (c.cityRank < minRank) return { color: 'yellow', why: `needs city rank ${minRank}` };
  }
  return { color: 'green', why: '' };
}

// ─── The map's controls ─────────────────────────────────────────────────────────────────────
/** SWG: a right-click turns the footprint 90° CLOCKWISE as seen on the face-down map (north up).
 *  three's +yaw is counter-clockwise seen from above, so clockwise is −90°. Degrees in, degrees out. */
export function rotateClockwise(yawDeg: number, stepDeg = 90): number { return ((yawDeg - stepDeg) % 360 + 360) % 360; }
export function rotateBy(yawDeg: number, deltaDeg: number): number { return ((yawDeg + deltaDeg) % 360 + 360) % 360; }

/** How high the map camera stands over the player: the footprint's diagonal ×2.4 with room to read the
 *  neighbourhood, never under 55 m nor over 140 m (the terrain window is only a few chunks). */
export function viewHeightFor(type: string): number {
  const fp = footprintOf(type);
  const diag = Math.hypot(fp.hx * 2, fp.hz * 2);
  return Math.max(55, Math.min(140, diag * 2.4 + 24));
}

/** The map's cursor: where a ray from a face-down camera at (camX, camY, camZ) through the pointer meets the
 *  ground — two fixed-point iterations against the plane at the ground under the previous estimate. */
export function groundUnderRay(
  origin: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number },
  groundY: (x: number, z: number) => number,
): { x: number; y: number; z: number } | null {
  if (dir.y >= -1e-4) return null;
  let y = groundY(origin.x, origin.z);
  let x = origin.x, z = origin.z;
  for (let i = 0; i < 8; i++) {
    const t = (y - origin.y) / dir.y;
    if (t <= 0) return null;
    x = origin.x + dir.x * t; z = origin.z + dir.z * t;
    const ny = groundY(x, z);
    const done = Math.abs(ny - y) < 1e-3;
    y = ny;
    if (done) break;
  }
  return { x, y, z };
}
